// src/components/sticky/StickyNotesPanel.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  X, Plus, Trash2, Edit3, Calendar, Bell, BellOff, NotebookPen, FileText,
} from 'lucide-react';
import { format, isPast, parseISO, isToday, isTomorrow } from 'date-fns';
import { fr } from 'date-fns/locale';
import toast from 'react-hot-toast';
import './StickyNotesPanel.css';


// ── Config priorités ──────────────────────────────────────────
const PRIORITY = {
  HIGH:   { label: 'Haute',   color: '#DC2626', bg: '#FFF1F1', light: 'rgba(239,68,68,0.22)' },
  MEDIUM: { label: 'Moyenne', color: '#D97706', bg: '#FFFBEB', light: '#FDE68A' },
  LOW:    { label: 'Basse',   color: '#16A34A', bg: 'rgba(34,197,94,0.08)', light: 'rgba(34,197,94,0.25)' },
};
const PRIORITY_ORDER = { HIGH: 0, MEDIUM: 1, LOW: 2 };


// ── Helpers ───────────────────────────────────────────────────
function genId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}
function storageKey(uid) { return `snp_notes_${uid}`; }
function loadNotes(uid) {
  try { return JSON.parse(localStorage.getItem(storageKey(uid)) || '[]'); }
  catch { return []; }
}
function persistNotes(uid, notes) {
  localStorage.setItem(storageKey(uid), JSON.stringify(notes));
}
function formatDue(iso) {
  const d = new Date(iso + 'T00:00:00');
  if (isToday(d))    return "Aujourd'hui";
  if (isTomorrow(d)) return 'Demain';
  return format(d, 'd MMM yyyy', { locale: fr });
}


// ── Modal create / edit ───────────────────────────────────────
function NoteModal({ note, onSave, onClose }) {
  const [text,       setText]       = useState(note?.text       || '');
  const [priority,   setPriority]   = useState(note?.priority   || 'MEDIUM');
  const [dueDate,    setDueDate]    = useState(note?.dueDate    || '');
  const [reminderAt, setReminderAt] = useState(note?.reminderAt || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    onSave({ text: text.trim(), priority, dueDate: dueDate || null, reminderAt: reminderAt || null });
  };

  return (
    <div className="snp-modal-backdrop" onClick={onClose}>
      <div className="snp-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="snp-modal-header">
          <div className="snp-modal-header-left">
            <div className="snp-modal-header-icon">
              {note ? <Edit3 size={15} /> : <Plus size={15} />}
            </div>
            <h3 className="snp-modal-title">
              {note ? 'Modifier la note' : 'Nouvelle note'}
            </h3>
          </div>
          <button className="snp-icon-btn" onClick={onClose} type="button">
            <X size={14} />
          </button>
        </div>

        <form className="snp-modal-body" onSubmit={handleSubmit}>

          {/* Texte */}
          <textarea
            className="snp-textarea"
            placeholder="Saisissez votre note…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            autoFocus
          />

          {/* Priorité */}
          <div className="snp-field-group">
            <label className="snp-label">Priorité</label>
            <div className="snp-priority-row">
              {Object.entries(PRIORITY).map(([key, cfg]) => (
                <button
                  key={key}
                  type="button"
                  className={`snp-prio-btn ${priority === key ? 'snp-prio-btn--active' : ''}`}
                  style={{ '--pc': cfg.color, '--pb': cfg.bg }}
                  onClick={() => setPriority(key)}
                >
                  <span className="snp-prio-indicator" style={{ '--pc': cfg.color }} />
                  {cfg.label}
                </button>
              ))}
            </div>
          </div>

          {/* Date limite + Rappel */}
          <div className="snp-fields-row">
            <div className="snp-field-group snp-field-group--half">
              <label className="snp-label">
                <Calendar size={11} /> Date limite
              </label>
              <input
                className="snp-input"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
            <div className="snp-field-group snp-field-group--half">
              <label className="snp-label">
                <Bell size={11} /> Rappel
              </label>
              <input
                className="snp-input"
                type="datetime-local"
                value={reminderAt}
                onChange={(e) => setReminderAt(e.target.value)}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="snp-modal-actions">
            <button className="snp-btn-secondary" type="button" onClick={onClose}>
              Annuler
            </button>
            <button className="snp-btn-primary" type="submit" disabled={!text.trim()}>
              {note ? 'Enregistrer' : 'Ajouter la note'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ── Carte note ────────────────────────────────────────────────
function NoteCard({ note, onEdit, onDelete }) {
  const cfg      = PRIORITY[note.priority] || PRIORITY.MEDIUM;
  const isOverdue = note.dueDate && isPast(new Date(note.dueDate + 'T23:59:59'));

  return (
    <div
      className="snp-card"
      style={{ '--ca': cfg.color, '--ca-bg': cfg.bg, '--ca-light': cfg.light }}
    >
      <div className="snp-card-accent" />
      <div className="snp-card-inner">

        {/* Header */}
        <div className="snp-card-head">
          <span className="snp-card-prio">
            <span className="snp-card-prio-dot" />
            {cfg.label}
          </span>
          <div className="snp-card-btns">
            <button className="snp-card-btn" onClick={() => onEdit(note)} title="Modifier">
              <Edit3 size={11} />
            </button>
            <button className="snp-card-btn snp-card-btn--del" onClick={() => onDelete(note.id)} title="Supprimer">
              <Trash2 size={11} />
            </button>
          </div>
        </div>

        {/* Texte */}
        <p className="snp-card-text">{note.text}</p>

        {/* Footer */}
        {(note.dueDate || note.reminderAt) && (
          <div className="snp-card-footer">
            {note.dueDate && (
              <span className={`snp-chip ${isOverdue ? 'snp-chip--overdue' : ''}`}>
                <Calendar size={10} />
                {formatDue(note.dueDate)}
              </span>
            )}
            {note.reminderAt && !note.reminderTriggered && (
              <span className="snp-chip snp-chip--reminder">
                <Bell size={10} />
                {format(parseISO(note.reminderAt), "d MMM 'à' HH:mm", { locale: fr })}
              </span>
            )}
            {note.reminderAt && note.reminderTriggered && (
              <span className="snp-chip snp-chip--done">
                <BellOff size={10} /> Rappelé
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ── Panel principal ───────────────────────────────────────────
export default function StickyNotesPanel({ isOpen, onClose }) {
  const { user } = useAuth();
  const [notes,  setNotes]  = useState([]);
  const [modal,  setModal]  = useState(null);
  const [filter, setFilter] = useState('ALL');
  const timerRef = useRef(null);

  useEffect(() => {
    if (user?.id) setNotes(loadNotes(user.id));
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) persistNotes(user.id, notes);
  }, [notes, user?.id]);

  // Rappels — check toutes les minutes
  useEffect(() => {
    if (!user?.id) return;
    const check = () => {
      setNotes((prev) => {
        let changed = false;
        const next = prev.map((n) => {
          if (n.reminderAt && !n.reminderTriggered && isPast(parseISO(n.reminderAt))) {
            toast(
              n.text.length > 72 ? n.text.slice(0, 72) + '…' : n.text,
              {
                duration: 9000,
                icon: '📌',
                style: {
                  background:   'rgba(9,23,56,0.97)',
                  color:        '#f1f5f9',
                  border:       '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '12px',
                  fontSize:     '13px',
                  fontWeight:   '500',
                },
              }
            );
            changed = true;
            return { ...n, reminderTriggered: true };
          }
          return n;
        });
        return changed ? next : prev;
      });
    };
    check();
    timerRef.current = setInterval(check, 60_000);
    return () => clearInterval(timerRef.current);
  }, [user?.id]);

  const handleSave = useCallback((data) => {
    setNotes((prev) => {
      if (modal && modal !== 'new') {
        const resetReminder = data.reminderAt !== modal.reminderAt;
        return prev.map((n) =>
          n.id === modal.id
            ? { ...n, ...data, reminderTriggered: resetReminder ? false : n.reminderTriggered }
            : n
        );
      }
      return [...prev, { id: genId(), ...data, reminderTriggered: false, createdAt: new Date().toISOString() }];
    });
    setModal(null);
  }, [modal]);

  const handleDelete = useCallback((id) => {
    if (!window.confirm('Supprimer cette note ?')) return;
    setNotes((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const visible = (filter === 'ALL' ? notes : notes.filter((n) => n.priority === filter))
    .slice()
    .sort((a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]);

  const countBy = (p) => notes.filter((n) => n.priority === p).length;

  const FILTERS = [
    { key: 'ALL',    label: 'Toutes',  color: '#3B82F6',  dot: null },
    { key: 'HIGH',   label: 'Haute',   color: '#DC2626',  dot: '#DC2626' },
    { key: 'MEDIUM', label: 'Moyenne', color: '#D97706',  dot: '#D97706' },
    { key: 'LOW',    label: 'Basse',   color: '#16A34A',  dot: '#16A34A' },
  ];

  return (
    <>
      <div className={`snp-backdrop ${isOpen ? 'snp-backdrop--on' : ''}`} onClick={onClose} />

      <aside className={`snp-panel ${isOpen ? 'snp-panel--open' : ''}`}>

        {/* Header */}
        <div className="snp-panel-header">
          <div className="snp-panel-header-left">
            <div className="snp-header-icon-wrap">
              <NotebookPen size={17} />
            </div>
            <div>
              <h2 className="snp-panel-title">Mes notes</h2>
              <p className="snp-panel-sub">
                {notes.length} note{notes.length !== 1 ? 's' : ''} · visibles uniquement par vous
              </p>
            </div>
          </div>
          <div className="snp-header-actions">
            <button className="snp-new-btn" onClick={() => setModal('new')} type="button">
              <Plus size={14} /> Nouvelle note
            </button>
            <button className="snp-icon-btn" onClick={onClose} type="button">
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div className="snp-filters">
          {FILTERS.map(({ key, label, color, dot }) => (
            <button
              key={key}
              className={`snp-filter ${filter === key ? 'snp-filter--active' : ''}`}
              style={{ '--fc': color }}
              onClick={() => setFilter(key)}
              type="button"
            >
              {dot && <span className="snp-filter-dot" style={{ background: dot }} />}
              {label}
              <span className="snp-filter-badge">
                {key === 'ALL' ? notes.length : countBy(key)}
              </span>
            </button>
          ))}
        </div>

        {/* Corps */}
        <div className="snp-body">
          {visible.length === 0 ? (
            <div className="snp-empty">
              <div className="snp-empty-icon">
                <FileText size={24} />
              </div>
              <p className="snp-empty-title">Aucune note</p>
              <p className="snp-empty-sub">
                {filter !== 'ALL'
                  ? 'Aucune note pour cette priorité.'
                  : 'Créez votre première note personnelle.'}
              </p>
              {filter === 'ALL' && (
                <button className="snp-btn-primary" onClick={() => setModal('new')} type="button">
                  <Plus size={13} /> Créer une note
                </button>
              )}
            </div>
          ) : (
            <div className="snp-grid">
              {visible.map((note) => (
                <NoteCard
                  key={note.id}
                  note={note}
                  onEdit={setModal}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      </aside>

      {modal && (
        <NoteModal
          note={modal === 'new' ? null : modal}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}
